<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import axios from 'axios'
import '@mux/mux-player'
import becomeATutor from '@/routes/become-a-tutor'
import teacher from '@/routes/teacher'

const props = defineProps({
  modelValue: {
    type: String,
    default: null // mux_upload_id
  },
  playbackId: {
    type: String,
    default: null // existing mux_playback_id for viewing
  },
  slug: {
    type: String,
    default: null // tutor application slug (for onboarding)
  },
  uploadUrlEndpoint: {
    type: String,
    default: null // if not provided, uses becomeATutor.muxUploadUrl (onboarding default)
  },
  statusUrlEndpoint: {
    type: String,
    default: null // if not provided, uses teacher.settings.media.muxUploadStatus
  },
  saveToProfile: {
    type: Boolean,
    default: false // whether to save to teacher profile when ready
  },
  error: {
    type: String,
    default: null
  },
  tall: {
    type: Boolean,
    default: false // false = 180px (matches image), true = 360px (for settings page stacked layout)
  }
})

// Parse size string (e.g. "200MB", "1GB") to bytes
const parseSizeToBytes = (size) => {
  const match = size.match(/^(\d+)\s*(MB|GB)$/i)
  if (!match) return 200 * 1024 * 1024 // fallback 200MB
  const value = parseInt(match[1])
  const unit = match[2].toUpperCase()
  return unit === 'GB' ? value * 1024 * 1024 * 1024 : value * 1024 * 1024
}

const page = usePage()
const maxSizeConfig = computed(() => page.props.config?.mux?.profile_video_max_size || '200MB')
const maxSizeBytes = computed(() => parseSizeToBytes(maxSizeConfig.value))

// Computed upload URL - use provided endpoint or default to onboarding route
const uploadUrl = computed(() => props.uploadUrlEndpoint || becomeATutor.muxUploadUrl.url())
// Computed status URL - use provided endpoint or default to settings route
const statusUrl = computed(() => props.statusUrlEndpoint || teacher.settings.media.muxUploadStatus.url())

// Computed height class based on tall prop
const heightClass = computed(() => props.tall ? 'min-h-[360px]' : 'min-h-[180px]')
const playerHeightClass = computed(() => props.tall ? 'h-[360px]' : 'h-[180px]')

const emit = defineEmits(['update:modelValue', 'update:playbackId', 'upload:start', 'upload:complete', 'upload:error', 'status:ready'])

// Upload state
const isUploading = ref(false)
const isUploaded = ref(false)
const uploadProgress = ref(0)
const fileInputRef = ref(null)
const uploadError = ref(null)

// Processing state
const processingStatus = ref(null) // 'checking', 'processing', 'ready', 'error', 'timeout'
const thumbnailUrl = ref(null)
const localPlaybackId = ref(null)
const statusCheckInterval = ref(null)
const statusCheckCount = ref(0)
const maxStatusChecks = 30 // Max 30 checks (about 2.5 minutes with 5s intervals)

// Computed - has a video ready to play
const hasPlayableVideo = computed(() => !!props.playbackId || !!localPlaybackId.value)
// Has an uploaded video (but may still be processing)
const hasUploadedVideo = computed(() => !!props.modelValue && !props.playbackId && !localPlaybackId.value)

// Watch for playbackId changes to reset uploaded state when video is ready
watch(() => props.playbackId, (newVal) => {
  if (newVal) {
    isUploaded.value = false
    localPlaybackId.value = newVal
    thumbnailUrl.value = `https://image.mux.com/${newVal}/thumbnail.jpg?time=0`
    processingStatus.value = 'ready'
    stopStatusPolling()
  }
})

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

// Status polling functions
const startStatusPolling = (uploadId) => {
  stopStatusPolling() // Clear any existing interval
  statusCheckCount.value = 0
  processingStatus.value = 'checking'

  // Initial check after 5 seconds
  setTimeout(() => {
    checkUploadStatus(uploadId)
  }, 5000)

  // Then check every 5 seconds
  statusCheckInterval.value = setInterval(() => {
    checkUploadStatus(uploadId)
  }, 5000)
}

const stopStatusPolling = () => {
  if (statusCheckInterval.value) {
    clearInterval(statusCheckInterval.value)
    statusCheckInterval.value = null
  }
}

const checkUploadStatus = async (uploadId) => {
  if (!uploadId) return

  statusCheckCount.value++

  try {
    const response = await axios.post(statusUrl.value, {
      upload_id: uploadId,
      save_to_profile: props.saveToProfile
    })

    const data = response.data

    if (data.ready && data.playback_id) {
      // Video is ready
      processingStatus.value = 'ready'
      localPlaybackId.value = data.playback_id
      thumbnailUrl.value = data.thumbnail_url
      stopStatusPolling()
      emit('update:playbackId', data.playback_id)
      emit('status:ready', {
        playback_id: data.playback_id,
        thumbnail_url: data.thumbnail_url,
        duration: data.duration
      })
    } else if (data.upload_status === 'errored' || data.asset_status === 'errored') {
      // Error occurred
      processingStatus.value = 'error'
      uploadError.value = 'Video processing failed. Please try again.'
      stopStatusPolling()
      emit('upload:error', new Error('Video processing failed'))
    } else {
      // Still processing
      processingStatus.value = 'processing'

      // Stop polling after max checks
      if (statusCheckCount.value >= maxStatusChecks) {
        stopStatusPolling()
        processingStatus.value = 'timeout'
      }
    }
  } catch (error) {
    console.error('Status check failed:', error)
    // Don't stop polling on network errors, just log
  }
}

const handleFileSelect = async (e) => {
  const file = e.target.files[0]
  if (!file) return

  // Validate file type
  if (!file.type.startsWith('video/')) {
    uploadError.value = 'Please select a video file'
    return
  }

  // Validate file size
  if (file.size > maxSizeBytes.value) {
    uploadError.value = `Video must be smaller than ${maxSizeConfig.value}`
    return
  }

  isUploading.value = true
  uploadProgress.value = 0
  uploadError.value = null
  processingStatus.value = null
  thumbnailUrl.value = null
  localPlaybackId.value = null
  emit('upload:start')

  try {
    // Get Upload URL - build headers dynamically
    const headers = {
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
    }
    // Only include slug header for onboarding endpoint
    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await axios.post(uploadUrl.value, {}, { headers })

    if (!response.data.upload_url) throw new Error('Failed to get upload URL')

    const muxUploadUrl = response.data.upload_url
    const uploadId = response.data.upload_id

    emit('update:modelValue', uploadId)

    // Start UpChunk Upload
    const upload = window.UpChunk.createUpload({
      endpoint: muxUploadUrl,
      file: file,
      chunkSize: 5120, // 5MB chunks
    })

    upload.on('progress', (progress) => {
      uploadProgress.value = progress.detail
    })

    upload.on('success', () => {
      isUploading.value = false
      isUploaded.value = true
      emit('upload:complete', uploadId)
      // Start polling for status
      startStatusPolling(uploadId)
    })

    upload.on('error', (err) => {
      console.error(err)
      isUploading.value = false
      uploadError.value = 'Upload failed. Please try again.'
      emit('upload:error', err)
    })

  } catch (error) {
    console.error(error)
    isUploading.value = false
    uploadError.value = error.response?.data?.message || 'Error starting upload.'
    emit('upload:error', error)
  }
}

const resetUpload = () => {
  isUploaded.value = false
  processingStatus.value = null
  thumbnailUrl.value = null
  localPlaybackId.value = null
  stopStatusPolling()
  emit('update:modelValue', null)
  emit('update:playbackId', null)
  uploadError.value = null
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

const triggerFileInput = () => {
  fileInputRef.value?.click()
}

// Load UpChunk library
onMounted(() => {
  if (!window.UpChunk) {
    const script = document.createElement('script')
    script.src = 'https://unpkg.com/@mux/upchunk@3'
    script.async = true
    document.head.appendChild(script)
  }

  // Initialize local state from props
  if (props.playbackId) {
    localPlaybackId.value = props.playbackId
    thumbnailUrl.value = `https://image.mux.com/${props.playbackId}/thumbnail.jpg?time=0`
    processingStatus.value = 'ready'
  } else if (props.modelValue && !props.playbackId) {
    // Have upload ID but no playback ID - start polling
    isUploaded.value = true
    startStatusPolling(props.modelValue)
  }
})

onUnmounted(() => {
  stopStatusPolling()
})
</script>

<template>
  <div class="form-group">
    <label class="block text-sm font-semibold text-gray-700 mb-3">
      Profile Video
      <span class="text-gray-400 text-xs ml-1">(Optional)</span>
    </label>

    <!-- Error Message -->
    <div v-if="error || uploadError" class="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
      <div class="flex items-start gap-2">
        <span class="material-symbols-outlined text-red-600 text-sm mt-0.5">error</span>
        <p class="text-sm text-red-700">{{ error || uploadError }}</p>
      </div>
    </div>

    <!-- Existing Video Player (has playback ID - ready to play) -->
    <div
      v-if="hasPlayableVideo && !isUploading"
      :class="['relative w-full rounded-xl overflow-hidden bg-black shadow-lg', playerHeightClass]"
    >
      <mux-player
        stream-type="on-demand"
        :playback-id="localPlaybackId || playbackId"
        metadata-video-title="Profile Introduction"
        primary-color="#2563EB"
        class="w-full h-full"
      ></mux-player>

      <!-- Remove Button -->
      <button
        type="button"
        @click="resetUpload"
        class="absolute top-2 right-2 z-10 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
      >
        <span class="material-symbols-outlined text-base">close</span>
      </button>
    </div>

    <!-- Uploading State -->
    <div
      v-else-if="isUploading"
      :class="['border-2 border-gray-200 rounded-xl p-6 bg-gray-50 flex items-center justify-center', heightClass]"
    >
      <div class="flex flex-col items-center">
        <div class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3">
          <span class="material-symbols-outlined text-orange-500 text-xl animate-pulse">cloud_upload</span>
        </div>
        <p class="text-gray-700 font-semibold mb-3 text-sm">Uploading Video...</p>
        <div class="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div
            class="bg-orange-500 h-2 rounded-full transition-all duration-300"
            :style="`width: ${uploadProgress}%`"
          ></div>
        </div>
        <p class="text-xs text-gray-500">{{ Math.round(uploadProgress) }}%</p>
      </div>
    </div>

    <!-- Uploaded State (Processing - has upload_id but no playback_id yet) -->
    <div
      v-else-if="isUploaded || hasUploadedVideo"
      :class="['relative rounded-xl overflow-hidden bg-black shadow-lg', playerHeightClass]"
    >
      <!-- Show thumbnail when available -->
      <template v-if="thumbnailUrl">
        <img
          :src="thumbnailUrl"
          alt="Video thumbnail"
          class="w-full h-full object-cover"
        />
        <!-- Processing overlay -->
        <div
          v-if="processingStatus === 'processing' || processingStatus === 'checking'"
          class="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white"
        >
          <span class="material-symbols-outlined text-2xl mb-2 animate-spin">sync</span>
          <span class="font-medium text-sm">Finalizing video...</span>
          <span class="text-xs text-gray-300 mt-1">Almost ready</span>
        </div>
        <!-- Ready indicator -->
        <div
          v-else-if="processingStatus === 'ready'"
          class="absolute bottom-3 left-3 bg-green-500/90 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1"
        >
          <span class="material-symbols-outlined text-sm">check_circle</span>
          Ready
        </div>
      </template>

      <!-- Fallback: No thumbnail yet, show loading state -->
      <template v-else>
        <div class="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white">
          <span class="material-symbols-outlined text-2xl mb-2 animate-spin">sync</span>
          <span class="font-medium text-sm">Video uploaded!</span>
          <span class="text-xs text-gray-400 mt-1">Processing...</span>
        </div>
      </template>

      <!-- Error State -->
      <div
        v-if="processingStatus === 'error'"
        class="absolute inset-0 bg-red-900/80 flex flex-col items-center justify-center text-white"
      >
        <span class="material-symbols-outlined text-2xl mb-2">error</span>
        <span class="font-medium text-sm">Processing failed</span>
        <span class="text-xs text-gray-300 mt-1">Please try again</span>
      </div>

      <!-- Timeout State -->
      <div
        v-if="processingStatus === 'timeout'"
        class="absolute inset-0 bg-orange-900/80 flex flex-col items-center justify-center text-white"
      >
        <span class="material-symbols-outlined text-2xl mb-2">schedule</span>
        <span class="font-medium text-sm">Taking longer than expected</span>
        <span class="text-xs text-gray-300 mt-1">Your video will be ready soon</span>
      </div>

      <!-- Remove Button -->
      <button
        type="button"
        @click="resetUpload"
        class="absolute top-2 right-2 z-10 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
      >
        <span class="material-symbols-outlined text-base">close</span>
      </button>
    </div>

    <!-- Upload Area (no video at all) -->
    <div
      v-else
      @click="triggerFileInput"
      :class="['border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-brand-blue transition-all cursor-pointer bg-gray-50 hover:bg-blue-50 flex items-center justify-center', heightClass]"
    >
      <div>
        <div class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <span class="material-symbols-outlined text-orange-500 text-xl">videocam</span>
        </div>
        <p class="text-gray-700 font-semibold mb-1 text-sm">Upload Video</p>
        <p class="text-xs text-gray-400">MP4, MOV (Max. {{ maxSizeConfig }})</p>
      </div>
    </div>

    <!-- Hidden File Input -->
    <input
      ref="fileInputRef"
      type="file"
      accept="video/mp4,video/quicktime,video/x-msvideo,.mp4,.mov,.avi"
      class="hidden"
      @change="handleFileSelect"
    >

    <!-- Helper Text -->
    <div class="mt-2 flex items-center space-x-2">
      <span class="material-symbols-outlined text-orange-500 text-xs">info</span>
      <p class="text-xs text-gray-600 leading-relaxed">30-60 second introduction video (optional)</p>
    </div>
  </div>
</template>
